import { Injectable } from '@angular/core';
import { BackendResponse, BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../shared/services/auth.service';
import { UserPreview } from '../shared/interfaces/user-preview.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SharedService } from '../shared/services/shared.service';
import { JobProgressNotification } from 'src/app/shared/modals/job-follow-up/models/job-progress-notification';
import { ReportType } from './interfaces/report.interface';
import { objectToBackendParams } from 'auth/src/lib/utils/filterUtils';
import { ReportFilters } from './model/report-filters';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Injectable({ providedIn: 'root' })
export class ReportsService {
    apiUrl: string;

    constructor(private _httpService: BackendService,
        private authService: AuthService,
        private http: HttpClient,
        private _sharedService: SharedService,
        private _configService: ConfigService
    ) {
        this.apiUrl = this._configService.settings.apiUrl;
    }

    public getUserCycles(): Observable<any> {
        return this._httpService.get('getReportCycles');
    }

    public getUsersForReport(searchString: string, pageSize: number): Observable<any> {
        return this._httpService.get('getUsersForReport', [], [
            { 'name': 'searchString', 'value': searchString as any },
            { 'name': 'pageSize', 'value': pageSize as any },
        ]);
    }

    public getUsersByEmployee(employeeNumbers: string): Observable<any> {
        return this._httpService.post('getUsersByEmployee', { 'Data': employeeNumbers });
    }

    public getAvailableReports(): Observable<any> {
        return this._httpService.get('getAvailableReports');
    }

    public getOptionalHeaderFields(): Observable<any> {
        return this._httpService.get('getOptionalHeaderFields');
    }

    public getCycleGroupedAttributes(cycleId: number): Observable<any> {
        return this._httpService.get('getGroupedAttributes', [],
            [{ 'name': 'cycleId', 'value': cycleId as any }]);
    }

    public getReport(filters: ReportFilters): Observable<any> {
        return this.downloadFile(new ReportFilters(
            filters.optionalFields, 
            filters.cycleId, 
            filters.reportId,
            filters.directSubordinates,
            filters.userStatus,
            filters.attributeIds,
            filters.cycleProcessConfigId,
            filters.usersToGetReport,
            filters.language
        ));
    }
    /**
     * Get the report by email (async)
     * @param language default is en-US
     * @returns jobId the job id to get the report
     */
    public getReportByEmail(filters: ReportFilters): Observable<BackendResponse<string>> {
        const params = objectToBackendParams(filters);
        let reportUrlKey = "";
        switch (filters.reportId) {
            case ReportType.Feedback:
                reportUrlKey = `getFeedbackReportByEmail`;
                break;
            case ReportType.AllUsersReport:
                reportUrlKey = `getUsersReportByEmail`;
                break;
            case ReportType.ConsolidatedCalibrationReport:
                reportUrlKey = `getComparisonExportByEmail`;
                break;
            default:
                reportUrlKey = `getReportByEmail`;
                break;
        }
        return this._httpService.get(reportUrlKey, [], params);
    }

    downloadFile(data: any): any {
        let applicationsUrl = `${this.apiUrl}/api/report/getReport`;
        if (data.reportId === 13) {
            applicationsUrl = `${this.apiUrl}/api/report/getFeedbackReport`;
        }
        if (data.reportId === 20) {
            applicationsUrl = `${this.apiUrl}/api/report/getUsersReport`;
        }
        if (data.reportId === ReportType.ConsolidatedCalibrationReport) {
            applicationsUrl = `${this.apiUrl}/api/report/group/comparisonExport`;
        }
        const user = this.authService.getLoggedUser();
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': user.access_token,
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv'
            }),
            observe: 'response' as 'body',
            responseType: 'blob' as 'json'
        };

        const impersonatedUser: UserPreview | null = this.authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            options.headers['UserIdToImpersonate'] = impersonatedUser.userId;
        }
        return this.http.post(applicationsUrl, data, options)
            .pipe(map((res) => this._sharedService.downloadFile(res)));
    }

    public getJobStatus(jobId: string) {
        return this._httpService.get<JobProgressNotification>('getReportJobStatus',
            [jobId], []);
    }

    public getExcelJobStatus(jobId: string) {
        return this._httpService.get<JobProgressNotification>('getExcelReportJobStatus',
            [jobId], []);
    }

    public getUsersReport(reportId: number): Observable<any> {
        return this._httpService.post('getUsersReport', { 'reportId': reportId });
    }
}
